// 계정-개인 권한 조회 + 부여/회수 + Effective 미리보기 훅

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  getAccountEffectivePermissions,
  getAccountPermissions,
  grantAccountPermission,
  listPermissionsByCategory,
  revokeAccountPermission,
} from "../services/permissionService";
import { ACCOUNT_PERMISSION_QK } from "../constants/permission.constants";
import type { ServiceCategoryType } from "../types/permission.types";

function invalidateAccountPermissionCache(
  qc: QueryClient,
  accountGuid: string,
) {
  qc.invalidateQueries({
    queryKey: ACCOUNT_PERMISSION_QK.accountPermissions(accountGuid),
  });
  qc.invalidateQueries({
    queryKey: ACCOUNT_PERMISSION_QK.accountEffective(accountGuid),
  });
}

/** 서비스 카테고리별 권한 마스터 목록 */
export const usePermissionsByCategory = (category: ServiceCategoryType) =>
  useQuery({
    queryKey: ["permissions", category],
    queryFn: () => listPermissionsByCategory(category),
    staleTime: 5 * 60_000,
  });

/** 직원에게 직접 부여된 권한 목록 */
export const useAccountPermissions = (accountGuid: string | null) =>
  useQuery({
    queryKey: ACCOUNT_PERMISSION_QK.accountPermissions(accountGuid ?? ""),
    queryFn: () => getAccountPermissions(accountGuid!),
    enabled: !!accountGuid,
    gcTime: 0,
  });

/** 직원의 최종 권한 코드 목록 (역할 ∪ 개인) */
export const useAccountEffectivePermissions = (accountGuid: string | null) =>
  useQuery({
    queryKey: ACCOUNT_PERMISSION_QK.accountEffective(accountGuid ?? ""),
    queryFn: () => getAccountEffectivePermissions(accountGuid!),
    enabled: !!accountGuid,
    gcTime: 0,
  });

/** 직원에게 개인 권한 부여 — grantedByAccountGuid 자동 주입 */
export const useGrantAccountPermission = () => {
  const qc = useQueryClient();
  const grantedBy = useAuthStore((s) => s.user?.userGuid ?? null);

  return useMutation({
    mutationFn: (params: {
      accountGuid: string;
      permissionGuid: string;
      memo?: string;
    }) =>
      grantAccountPermission({
        AccountGuid: params.accountGuid,
        PermissionGuid: params.permissionGuid,
        Memo: params.memo,
        GrantedByAccountGuid: grantedBy,
      }),
    onSuccess: (_data, variables) => {
      invalidateAccountPermissionCache(qc, variables.accountGuid);
    },
  });
};

/** 직원의 개인 권한 회수 */
export const useRevokeAccountPermission = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (params: { accountGuid: string; permissionGuid: string }) =>
      revokeAccountPermission({
        AccountGuid: params.accountGuid,
        PermissionGuid: params.permissionGuid,
      }),
    onSuccess: (_data, variables) => {
      invalidateAccountPermissionCache(qc, variables.accountGuid);
    },
  });
};
